import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  CheckCircle2, 
  AlertCircle,
  Download,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [defaultCategory, setDefaultCategory] = useState("");
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith(".csv")) {
        toast.error("Please select a CSV file");
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith(".csv")) {
        toast.error("Please select a CSV file");
        return;
      }
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (defaultCategory.trim()) {
        formData.append("default_category", defaultCategory.trim().toLowerCase());
      }

      const response = await fetch(`${API}/leads/import-csv`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        if (data.imported > 0) {
          toast.success(`Successfully imported ${data.imported} leads`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Import failed");
      }
    } catch (error) {
      toast.error("Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = "business_name,address,city,state,zip_code,phone,email,website,category\n";
    const example = "Acme Corp,123 Main St,San Francisco,CA,94102,(555) 123-4567,contact@acme.com,https://acme.com,Technology\n";
    const blob = new Blob([headers + example], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="import-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
          Import Leads
        </h1>
        <p className="text-slate-500 mt-1">
          Upload a CSV file to bulk import leads
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upload area */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Upload CSV</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  file 
                    ? "border-primary bg-primary/5" 
                    : "border-slate-300 hover:border-slate-400 hover:bg-slate-50"
                }`}
                data-testid="drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="file-input"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-primary" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">
                      Drop your CSV file here or click to browse
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      Maximum file size: 5MB
                    </p>
                  </>
                )}
              </div>

              <div className="mt-4">
                <Label htmlFor="default-category" className="text-sm">
                  Default category <span className="text-xs text-slate-400">(optional)</span>
                </Label>
                <Input
                  id="default-category"
                  value={defaultCategory}
                  onChange={(e) => setDefaultCategory(e.target.value)}
                  placeholder="e.g. restaurant, college, dentist"
                  data-testid="import-default-category"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Applied to any row missing a category. If your CSV has a <code>category</code> column, that value wins. Leave blank and missing rows become <code>uncategorized</code>.
                </p>
              </div>

              <div className="mt-4 flex gap-3">
                <Button
                  onClick={handleImport}
                  disabled={!file || importing}
                  className="flex-1"
                  data-testid="import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Leads
                    </>
                  )}
                </Button>
                {file && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFile(null);
                      setResult(null);
                    }}
                    data-testid="clear-btn"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {/* Results */}
              {result && (
                <div className="mt-6 p-4 rounded-lg border border-slate-200 bg-slate-50" data-testid="import-result">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="font-medium text-slate-900">
                      Import Complete
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">
                    Successfully imported <strong>{result.imported}</strong> leads
                  </p>
                  {result.errors?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {result.errors.length} errors
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-slate-600 max-h-32 overflow-y-auto">
                        {result.errors.map((error, i) => (
                          <li key={i} className="text-red-600">{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">CSV Format</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Your CSV file should include the following columns:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <strong>business_name</strong> (required)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                  address
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                  city
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                  state
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                  zip_code
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                  phone
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                  email
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                  website
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                  category
                </li>
              </ul>
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={downloadTemplate}
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Tips</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-slate-600">
                <li>• Column headers are case-insensitive</li>
                <li>• Alternative column names are supported (e.g., "Name" for "business_name")</li>
                <li>• Empty rows will be skipped</li>
                <li>• Duplicate phone numbers are allowed</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
